import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, X, FileText, ExternalLink, History, ChevronDown } from "lucide-react";
import { api, API, apiError, TOKEN_KEY } from "@/lib/api";

const STATUS_COLORS = {
  draft: "bg-gray-100 text-gray-600", submitted: "bg-amber-100 text-amber-700",
  under_review: "bg-amber-100 text-amber-700", action_required: "bg-orange-100 text-orange-700",
  additional_document: "bg-orange-100 text-orange-700", approved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-700", suspended: "bg-red-100 text-red-700",
};
const ACTIONS = [
  { key: "approve", label: "Approve", cls: "bg-emerald-600" },
  { key: "request_correction", label: "Request Correction", cls: "bg-orange-500", needReason: true },
  { key: "request_document", label: "Request Document", cls: "bg-orange-500", needReason: true },
  { key: "reject", label: "Reject", cls: "bg-red-600", needReason: true },
  { key: "suspend", label: "Suspend", cls: "bg-red-500", needReason: true },
  { key: "reactivate", label: "Reactivate", cls: "bg-emerald-500" },
];

export default function AdminVerification() {
  const [roleF, setRoleF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [data, setData] = useState({ applications: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [reason, setReason] = useState("");
  const [acting, setActing] = useState(false);

  const load = () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (roleF) p.set("role", roleF);
    if (statusF) p.set("status", statusF);
    api.get(`/admin/verifications?${p}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [roleF, statusF]);

  const openDetail = async (uid) => {
    setReason("");
    try { const { data } = await api.get(`/admin/verifications/${uid}`); setDetail(data); } catch (e) { toast.error(apiError(e)); }
  };

  const act = async (action, needReason) => {
    if (needReason && !reason.trim()) return toast.error("Please provide a reason");
    setActing(true);
    try {
      await api.post(`/admin/verifications/${detail.applicant.id}/action`, { action, reason });
      toast.success(`Application ${action.replace("_", " ")}`);
      setDetail(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setActing(false); }
  };

  const token = localStorage.getItem(TOKEN_KEY);

  return (
    <div data-testid="admin-verification">
      <div className="bg-white rounded-2xl border border-border p-4">
        <div className="flex items-center gap-2 mb-3"><ShieldCheck className="text-coral" size={20} /><h2 className="font-display font-bold text-lg">Verification Center</h2></div>
        <div className="flex flex-wrap gap-2 mb-3">
          {Object.entries(data.counts).map(([k, v]) => <span key={k} className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[k] || "bg-gray-100"}`}>{k.replace("_", " ")}: {v}</span>)}
        </div>
        <div className="flex gap-2 mb-3">
          <select value={roleF} onChange={(e) => setRoleF(e.target.value)} className="bg-surface rounded-lg px-3 py-2 text-sm" data-testid="verif-role-filter"><option value="">All roles</option><option value="restaurant">Restaurant</option><option value="delivery">Delivery</option></select>
          <select value={statusF} onChange={(e) => setStatusF(e.target.value)} className="bg-surface rounded-lg px-3 py-2 text-sm" data-testid="verif-status-filter"><option value="">All statuses</option>{["draft", "submitted", "under_review", "action_required", "additional_document", "approved", "rejected", "suspended"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select>
        </div>
        {loading ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-coral" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-ink-600 border-b border-border">{["Name", "Role", "Phone", "Status", ""].map((c) => <th key={c} className="py-2 font-semibold">{c}</th>)}</tr></thead>
              <tbody>
                {data.applications.map((a) => (
                  <tr key={a.id} className="border-b border-border last:border-0" data-testid={`verif-row-${a.id}`}>
                    <td className="py-2 font-semibold">{a.name || "—"}</td>
                    <td className="py-2">{a.role}</td>
                    <td className="py-2">{a.phone || "—"}</td>
                    <td className="py-2"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[a.onboarding_status] || "bg-gray-100"}`}>{(a.onboarding_status || "draft").replace("_", " ")}</span></td>
                    <td className="py-2"><button onClick={() => openDetail(a.id)} className="text-coral font-semibold" data-testid={`verif-review-${a.id}`}>Review</button></td>
                  </tr>
                ))}
                {!data.applications.length && <tr><td colSpan={5} className="py-8 text-center text-ink-600">No applications</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-[90] bg-black/50 flex justify-end" onClick={() => setDetail(null)}>
          <div className="bg-surface w-full max-w-[460px] h-full overflow-y-auto p-4" onClick={(e) => e.stopPropagation()} data-testid="verif-detail">
            <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold text-lg">{detail.applicant.name}</h3><button onClick={() => setDetail(null)}><X size={20} /></button></div>
            <Section title="Application">
              <KV k="Role" v={detail.applicant.role} /><KV k="Status" v={(detail.applicant.onboarding_status || "").replace("_", " ")} />
              <KV k="Phone" v={detail.applicant.phone} /><KV k="Email" v={detail.applicant.email} />
            </Section>
            {detail.applicant.onboarding && Object.entries(detail.applicant.onboarding).map(([sec, obj]) => obj && typeof obj === "object" && (
              <Section key={sec} title={sec}>{Object.entries(obj).map(([k, v]) => typeof v !== "object" && <KV key={k} k={k} v={String(v)} />)}</Section>
            ))}
            <Section title={`Documents (${detail.documents.length})`}>
              {detail.documents.length ? detail.documents.map((d) => <DocRow key={d.id} d={d} token={token} />) : <p className="text-sm text-ink-600">No documents uploaded</p>}
            </Section>
            <div className="bg-white rounded-xl border border-border p-3 mt-3">
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason / message to partner (required for reject/correction/suspend)" className="w-full bg-surface rounded-lg p-2 text-sm outline-none" rows={2} data-testid="verif-reason" />
              <div className="grid grid-cols-2 gap-2 mt-2">
                {ACTIONS.map((a) => <button key={a.key} disabled={acting} onClick={() => act(a.key, a.needReason)} className={`${a.cls} text-white font-semibold py-2 rounded-lg text-xs`} data-testid={`verif-action-${a.key}`}>{a.label}</button>)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function expiryBadge(d) {
  const exp = d.expiry_date ? new Date(d.expiry_date) : null;
  const days = exp ? Math.ceil((exp - new Date()) / 86400000) : null;
  if (days === null) return null;
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${days < 0 ? "bg-red-100 text-red-600" : days <= 14 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{days < 0 ? "Expired" : `${days}d left`}</span>;
}
function fmtDate(s) { try { return new Date(s).toLocaleString([], { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } }

function DocRow({ d, token }) {
  const [open, setOpen] = useState(false);
  const versions = d.versions || [];
  return (
    <div className="mb-2" data-testid={`verif-doc-${d.id}`}>
      <a href={`${API}/files/${d.id}/download?auth=${token}`} target="_blank" rel="noreferrer" className="flex items-center justify-between bg-white rounded-lg px-3 py-2 text-sm border border-border">
        <span className="flex items-center gap-2 text-ink min-w-0">
          <FileText size={14} className="shrink-0" />
          <span className="truncate">{d.purpose.replace("kyc:", "").replace("_", " ")} · {d.original_filename}</span>
          <span className="text-[10px] font-bold text-coral bg-coral/10 px-1.5 py-0.5 rounded shrink-0">v{d.document_version || 1}</span>
        </span>
        <span className="flex items-center gap-2 shrink-0">{expiryBadge(d)}<ExternalLink size={14} className="text-coral" /></span>
      </a>
      {versions.length > 0 && (
        <div className="mt-1 ml-1">
          <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-1.5 text-[11px] font-semibold text-ink-600 hover:text-coral" data-testid={`verif-doc-history-toggle-${d.id}`}>
            <History size={12} /> Version history ({versions.length}) <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <div className="mt-1 space-y-1 border-l-2 border-border pl-2" data-testid={`verif-doc-history-${d.id}`}>
              {versions.map((v) => (
                <a key={v.id} href={`${API}/files/${v.id}/download?auth=${token}`} target="_blank" rel="noreferrer" className="flex items-center justify-between bg-surface rounded-lg px-2.5 py-1.5 text-[11px] text-ink-600" data-testid={`verif-doc-version-${v.id}`}>
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[10px] font-bold text-gray-500 bg-gray-200 px-1.5 py-0.5 rounded shrink-0">v{v.document_version || 1}</span>
                    <span className="truncate">{v.original_filename}</span>
                    <span className="text-[9px] font-semibold px-1 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">archived</span>
                  </span>
                  <span className="flex items-center gap-1.5 shrink-0 whitespace-nowrap">{fmtDate(v.archived_at || v.created_at)}<ExternalLink size={11} className="text-coral" /></span>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) { return <div className="bg-white rounded-xl border border-border p-3 mt-3"><div className="text-xs font-bold text-coral uppercase mb-1.5">{title}</div>{children}</div>; }
function KV({ k, v }) { return <div className="flex justify-between text-sm py-0.5"><span className="text-ink-600 capitalize">{k.replace("_", " ")}</span><span className="font-semibold text-ink text-right">{v || "—"}</span></div>; }
